import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    Button, Card, CardContent, CardHeader, Checkbox, CircularProgress, Divider, FormControl,
    FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Typography,
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import uuid from "react-uuid";
import { ApiBackend } from "../../providers/apibackend";
import ProductList from "../Common/ProductList/ProductList";
import { ProductListChangeEvent } from "../Common/ProductList/utils";
import StartGroupSelector from "../Common/StartGroupSelector";
import {
    Offer, OfferChannel, OfferChannelType, OfferChannelTypes, OfferEligibilityImplementation,
    OfferEligibilityImplementations, OfferEligibilityProduct, OfferGender, OfferGenders,
    OfferReward, OfferRewardType, OfferRewardTypes,
    OFFER_CHANNEL_LABELS, OFFER_GENDER_LABELS, OFFER_IMPLEMENTATION_LABELS, OFFER_REWARD_LABELS,
} from "../../model/Offer";

const blankChannel = (channel: OfferChannelType): OfferChannel => ({
    id: uuid(), channel, isActive: false, activeFrom: "", activeTo: "",
    title: "", titleEn: "", shortDescription: "", shortDescriptionEn: "",
    longDescription: "", longDescriptionEn: "", heroImageUrl: "", buttonText: "", buttonTextEn: "", sortIndex: 0,
});

// Säkerställ att alla tre kanaltyper finns (en rad per kanal i formuläret) och att alla fält har värden.
const ensureAllChannels = (channels: OfferChannel[]): OfferChannel[] =>
    OfferChannelTypes.map((ct) => {
        const existing = (channels || []).find((c) => c.channel === ct);
        return existing ? { ...blankChannel(ct), ...existing } : blankChannel(ct);
    });

const newOffer = (): Offer => ({
    id: uuid(), name: "", isArchived: false,
    eligibilityImplementation: "cart_contains", eligibilityGender: "both", hideIfRewardPurchased: false,
    channels: ensureAllChannels([]), eligibilityProducts: [], rewards: [],
});

// MySQL-datetime / ISO -> datetime-local-input ("YYYY-MM-DDTHH:mm").
const toLocalInput = (v?: string): string => (v ? String(v).replace(" ", "T").slice(0, 16) : "");

const EditOffer = () => {
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();
    const [offer, setOffer] = useState(null as Offer);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [channelTab, setChannelTab] = useState(0);

    useEffect(() => {
        if (id) {
            new ApiBackend().getOffer(id).then((res) => {
                if (res) {
                    res.channels = ensureAllChannels(res.channels);
                    setOffer(res);
                } else {
                    setOffer(newOffer());
                }
                setLoading(false);
            });
        } else {
            setOffer(newOffer());
            setLoading(false);
        }
    }, [id]);

    const update = (patch: Partial<Offer>) => setOffer((o) => ({ ...o, ...patch }));
    const updateChannel = (ct: OfferChannelType, patch: Partial<OfferChannel>) =>
        setOffer((o) => ({ ...o, channels: o.channels.map((c) => (c.channel === ct ? { ...c, ...patch } : c)) }));

    const addEligibilityProduct = () =>
        update({ eligibilityProducts: [...offer.eligibilityProducts, { id: uuid(), productId: "" }] });
    const updateEligibilityProduct = (idx: number, patch: Partial<OfferEligibilityProduct>) =>
        update({ eligibilityProducts: offer.eligibilityProducts.map((e, i) => (i === idx ? { ...e, ...patch } : e)) });
    const removeEligibilityProduct = (idx: number) =>
        update({ eligibilityProducts: offer.eligibilityProducts.filter((_, i) => i !== idx) });

    const save = async () => {
        setSaving(true);
        // Spara inte en tom belöning (saknar produkt resp. länk).
        const cleanedRewards = (offer.rewards || []).filter(r =>
            (r.rewardType === "buy_product" && !!r.productId) ||
            (r.rewardType === "external_link" && !!r.url));
        const ok = await new ApiBackend().createOrUpdateOffer({ ...offer, rewards: cleanedRewards });
        setSaving(false);
        if (ok) { history.push("/offers/manage"); } else { alert("Kunde inte spara erbjudandet"); }
    };

    const remove = async () => {
        if (!window.confirm("Ta bort erbjudandet?")) { return; }
        const ok = await new ApiBackend().deleteOffer(offer.id);
        if (ok) { history.push("/offers/manage"); } else { alert("Kunde inte ta bort erbjudandet"); }
    };

    if (loading || !offer) { return <CircularProgress color="secondary" />; }

    const isStartGroup = offer.eligibilityImplementation === "start_group";

    // Belöning (ett erbjudande har en belöning): köp produkt eller länk till extern produkt.
    const reward: OfferReward | undefined = offer.rewards[0];
    const rewardType: OfferRewardType = reward?.rewardType || "buy_product";
    const updateReward = (patch: Partial<OfferReward>) =>
        update({ rewards: [{ id: reward?.id || uuid(), rewardType, productId: "", ...(reward || {}), ...patch }] });
    const setRewardType = (rt: OfferRewardType) =>
        updateReward(rt === "buy_product"
            ? { rewardType: rt, url: undefined, linkText: undefined, linkTextEn: undefined }
            : { rewardType: rt, productId: "" });

    const sectionHeader = (title: string) => (
        <Typography variant="subtitle2" style={{ marginTop: 20, marginBottom: 4, textTransform: "uppercase", color: "#555", letterSpacing: 0.5 }}>
            {title}
        </Typography>
    );

    const ch = offer.channels[channelTab];

    return (
        <Card>
            <CardHeader title={id ? "Redigera erbjudande" : "Nytt erbjudande"} />
            <CardContent style={{ paddingTop: 0 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={8}>
                        <TextField label="Namn" fullWidth value={offer.name} onChange={(e) => update({ name: e.target.value })} />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={<Checkbox checked={offer.isArchived} onChange={(e) => update({ isArchived: e.target.checked })} />}
                            label="Arkiverad"
                        />
                    </Grid>
                </Grid>

                {sectionHeader("Publiceringskanaler")}
                <Tabs value={channelTab} onChange={(_, v) => setChannelTab(v)} textColor="inherit" variant="fullWidth">
                    {offer.channels.map((c) => (
                        <Tab key={c.channel} label={OFFER_CHANNEL_LABELS[c.channel] + (c.isActive ? " ●" : "")} />
                    ))}
                </Tabs>
                {ch &&
                    <div style={{ border: "1px solid #e0e0e0", borderTop: "none", padding: 16 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                                <FormControlLabel control={<Checkbox checked={ch.isActive} onChange={(e) => updateChannel(ch.channel, { isActive: e.target.checked })} />} label="Aktiv i denna kanal" />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField label="Aktiv från (valfritt)" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }}
                                    value={toLocalInput(ch.activeFrom)} onChange={(e) => updateChannel(ch.channel, { activeFrom: e.target.value })} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField label="Aktiv t.o.m. (valfritt)" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }}
                                    value={toLocalInput(ch.activeTo)} onChange={(e) => updateChannel(ch.channel, { activeTo: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Rubrik (svenska)" fullWidth value={ch.title} onChange={(e) => updateChannel(ch.channel, { title: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Rubrik (engelska)" fullWidth value={ch.titleEn} onChange={(e) => updateChannel(ch.channel, { titleEn: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Kort beskrivning (svenska)" fullWidth value={ch.shortDescription} onChange={(e) => updateChannel(ch.channel, { shortDescription: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Kort beskrivning (engelska)" fullWidth value={ch.shortDescriptionEn} onChange={(e) => updateChannel(ch.channel, { shortDescriptionEn: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Lång beskrivning (svenska)" fullWidth multiline rows={3} value={ch.longDescription} onChange={(e) => updateChannel(ch.channel, { longDescription: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Lång beskrivning (engelska)" fullWidth multiline rows={3} value={ch.longDescriptionEn} onChange={(e) => updateChannel(ch.channel, { longDescriptionEn: e.target.value })} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label="Hero-bild (URL)" fullWidth value={ch.heroImageUrl} onChange={(e) => updateChannel(ch.channel, { heroImageUrl: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Köpknappstext (svenska)" fullWidth value={ch.buttonText} onChange={(e) => updateChannel(ch.channel, { buttonText: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Köpknappstext (engelska)" fullWidth value={ch.buttonTextEn} onChange={(e) => updateChannel(ch.channel, { buttonTextEn: e.target.value })} />
                            </Grid>
                        </Grid>
                    </div>
                }

                {sectionHeader("Krav för erbjudande")}
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Implementation</InputLabel>
                            <Select value={offer.eligibilityImplementation}
                                onChange={(e) => update({ eligibilityImplementation: e.target.value as OfferEligibilityImplementation })}>
                                {OfferEligibilityImplementations.map((i) => <MenuItem key={i} value={i}>{OFFER_IMPLEMENTATION_LABELS[i]}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Kön</InputLabel>
                            <Select value={offer.eligibilityGender}
                                onChange={(e) => update({ eligibilityGender: e.target.value as OfferGender })}>
                                {OfferGenders.map((g) => <MenuItem key={g} value={g}>{OFFER_GENDER_LABELS[g]}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {offer.eligibilityProducts.length > 0 &&
                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                        <Grid item xs={isStartGroup ? 5 : 10}><Typography variant="caption" color="textSecondary">Produkt</Typography></Grid>
                        {isStartGroup && <Grid item xs={5}><Typography variant="caption" color="textSecondary">Startled</Typography></Grid>}
                        <Grid item xs={2} />
                    </Grid>
                }
                {offer.eligibilityProducts.map((e, idx) => (
                    <Grid container spacing={2} key={e.id || idx} alignItems="center">
                        <Grid item xs={isStartGroup ? 5 : 10}>
                            <ProductList initialValue={e.productId}
                                onChange={(evt: ProductListChangeEvent) => updateEligibilityProduct(idx, { productId: (evt.products[0] && evt.products[0].id) || "", startGroupId: undefined })} />
                        </Grid>
                        {isStartGroup &&
                            <Grid item xs={5}>
                                {e.productId
                                    ? <StartGroupSelector productIds={e.productId} initialValue={e.startGroupId} clearable
                                        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => updateEligibilityProduct(idx, { startGroupId: evt.target.value })} />
                                    : <Typography variant="caption" color="textSecondary">Välj produkt först</Typography>}
                            </Grid>
                        }
                        <Grid item xs={2}><IconButton size="small" onClick={() => removeEligibilityProduct(idx)}><Delete fontSize="small" /></IconButton></Grid>
                    </Grid>
                ))}
                <Button size="small" onClick={addEligibilityProduct}>+ Lägg till produkt</Button>

                {sectionHeader("Erbjudande")}
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>Typ av erbjudande</InputLabel>
                            <Select value={rewardType} onChange={(e) => setRewardType(e.target.value as OfferRewardType)}>
                                {OfferRewardTypes.map((rt) => <MenuItem key={rt} value={rt}>{OFFER_REWARD_LABELS[rt]}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {rewardType === "buy_product" &&
                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={6}>
                            <InputLabel shrink>Produkt man får möjlighet att handla</InputLabel>
                            <ProductList initialValue={reward?.productId || ""} clearable
                                onChange={(evt: ProductListChangeEvent) => updateReward({ productId: (evt.products[0] && evt.products[0].id) || "" })}
                                onClear={() => updateReward({ productId: "" })} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={offer.hideIfRewardPurchased} onChange={(e) => update({ hideIfRewardPurchased: e.target.checked })} />}
                                label="Dölj erbjudandet när personen redan köpt produkten ovan"
                            />
                        </Grid>
                    </Grid>
                }

                {rewardType === "external_link" &&
                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={12}>
                            <TextField label="Länk (URL, öppnas i ny flik)" fullWidth value={reward?.url || ""}
                                onChange={(e) => updateReward({ url: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Knapptext (svenska)" fullWidth value={reward?.linkText || ""}
                                onChange={(e) => updateReward({ linkText: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField label="Knapptext (engelska)" fullWidth value={reward?.linkTextEn || ""}
                                onChange={(e) => updateReward({ linkTextEn: e.target.value })} />
                        </Grid>
                    </Grid>
                }

                <Divider style={{ margin: "20px 0 16px" }} />
                <Button variant="contained" color="secondary" disabled={saving || !offer.name} onClick={save}>
                    {saving ? "Sparar..." : "Spara"}
                </Button>
                {id && <Button style={{ marginLeft: 12 }} onClick={remove}>Ta bort</Button>}
                <Button style={{ marginLeft: 12 }} onClick={() => history.push("/offers/manage")}>Avbryt</Button>
            </CardContent>
        </Card>
    );
};

export default EditOffer;
